import { useState } from 'react';
import { Fingerprint, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVault } from './VaultContext';
import { toast } from 'sonner';

interface BiometricUnlockProps {
  onSuccess?: () => void;
}

/**
 * Button to unlock vault using biometrics (FaceID/TouchID).
 * Only renders when biometric is enabled.
 */
export function BiometricUnlock({ onSuccess }: BiometricUnlockProps) {
  const { unlockWithBiometric, disableBiometric, isBiometricEnabled, biometricEmail, isLoading } = useVault();
  const [isUnlocking, setIsUnlocking] = useState(false);

  if (!isBiometricEnabled) {
    return null;
  }

  const handleUnlock = async () => {
    setIsUnlocking(true);
    try {
      await unlockWithBiometric();
      onSuccess?.();
    } catch (error) {
      console.error('Biometric unlock failed:', error);
      // Show user-friendly toast based on error type
      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('расшифровать')) {
          toast.error('Биометрия сброшена', {
            description: 'Войдите с паролем и настройте FaceID заново',
          });
        } else if (msg.includes('not allowed') || msg.includes('notallowed')) {
          toast.error('Face ID недоступен', {
            description: 'Используйте пароль или отключите Face ID',
          });
        }
      }
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleDisable = () => {
    disableBiometric();
    toast.info('Face ID отключён', {
      description: 'Теперь используйте пароль для входа',
    });
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={handleUnlock}
        disabled={isUnlocking || isLoading}
        className="w-full"
        size="lg"
        variant="default"
      >
        {isUnlocking || isLoading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Проверка...
          </>
        ) : (
          <>
            <Fingerprint className="h-5 w-5 mr-2" />
            Разблокировать по FaceID
          </>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2">
        {biometricEmail && (
          <span className="text-xs text-muted-foreground">
            {biometricEmail}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground h-auto py-1 px-2"
          onClick={handleDisable}
          disabled={isUnlocking || isLoading}
        >
          <X className="h-3 w-3 mr-1" />
          Отключить
        </Button>
      </div>
    </div>
  );
}
